import { useState } from "react";
import { ArrowLeftRight, Undo } from "lucide-react";
import type { OrderProduct, ReplacementEntry } from "./types";
import ReplacementModal from "./ReplacementModal";

interface ApproveOrderTableProps {
  products: OrderProduct[];
  clinicId: string;
  replacements: Record<string, ReplacementEntry>;
  onReplace: (productId: string, entry: ReplacementEntry) => void;
  onRevert: (productId: string) => void;
  initialClinicStock?: any[];
  isSubstitutionAllowed?: boolean;
}

const ApproveOrderTable: React.FC<ApproveOrderTableProps> = ({ products, clinicId, replacements, onReplace, onRevert, initialClinicStock, isSubstitutionAllowed = true }) => {
  const [replacingProductId, setReplacingProductId] = useState<string | null>(null);
  const replacingProduct = products.find((p) => p.productId === replacingProductId);

  return (
    <div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="min-h-[320px]">
          <table className="w-full text-xs min-w-[600px]">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                {["S.No.", "Name", "Qty", "MRP", "Price", "Price Difference", "Status", ...(isSubstitutionAllowed ? ["Action"] : [])].map((h) => (
                  <th key={h} className="px-3 py-2 text-left font-semibold text-slate-500 text-[0.65rem] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products.map((product, productIndex) => {
                const replacement = replacements[product.productId];
                const isReplaced = !!replacement;
                return (
                  <tr key={product.productId} className="border-b border-slate-50 hover:bg-slate-50/80 transition-colors ">
                    <td className="px-3 py-2.5 text-xs font-medium text-slate-500">{productIndex + 1}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-2">
                        <img
                          src={isReplaced ? replacement.substituteImage : product.productImage}
                          alt=""
                          className="w-8 h-8 rounded-md object-cover bg-slate-100 border border-slate-100"
                          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                        />
                        <div>
                          <div className="font-semibold text-slate-800">
                            {isReplaced ? replacement.substituteName : product.name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 font-medium">{product.quantity}</td>
                    <td className="px-3 py-2.5">₹{isReplaced ? replacement.substituteMrp : product.productMrp}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">₹{isReplaced ? replacement.substitutePrice : product.productPrice}</td>
                    <td className="px-3 py-2.5 font-medium">
                      {(() => {
                        if (!isReplaced) return <span className="text-slate-300">—</span>;
                        const originalPrice = parseFloat(product.productPrice) || 0;
                        const substitutePrice = replacement.substitutePrice || 0;
                        const unitDiff = substitutePrice - originalPrice;
                        const totalDiff = unitDiff * product.quantity;

                        if (unitDiff === 0) {
                          return <span className="text-slate-500 font-bold bg-slate-50 border border-slate-100 rounded px-1.5 py-0.5 whitespace-nowrap">₹0.00</span>;
                        } else if (unitDiff < 0) {
                          return (
                            <span className="text-emerald-600 font-bold bg-emerald-50 border border-emerald-100 rounded px-1.5 py-0.5 whitespace-nowrap">
                              ₹{Math.abs(totalDiff).toFixed(2)} Reduced
                            </span>
                          );
                        } else {
                          return (
                            <span className="text-red-500 font-bold bg-red-50 border border-red-100 rounded px-1.5 py-0.5 whitespace-nowrap">
                              ₹{Math.abs(totalDiff).toFixed(2)} Increased
                            </span>
                          );
                        }
                      })()}
                    </td>
                    <td className="px-3 py-2.5">
                      {isReplaced ? (
                        <div className="relative group inline-block">
                          <span className="px-2 py-0.5 rounded-full text-[0.6rem] font-semibold bg-amber-100 text-amber-700 cursor-help">
                            Replaced
                          </span>
                          {/* Hover tooltip - aligned to right-0 and z-50 to avoid clipping */}
                          <div className="hidden group-hover:block absolute bottom-full right-0 mb-2 z-[100000] w-[240px]">
                            <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3">
                              <div className="text-[0.6rem] font-semibold text-amber-600 mb-1">Originally Ordered</div>
                              <div className="flex items-center gap-2 mb-2">
                                <img
                                  src={product.productImage}
                                  alt=""
                                  className="w-7 h-7 rounded-md object-cover bg-slate-100"
                                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                                />
                                <div>
                                  <div className="text-[0.65rem] font-semibold text-slate-800">{product.name}</div>
                                  <div className="text-[0.55rem] text-slate-400">Price: ₹{product.productPrice} | MRP: ₹{product.productMrp}</div>
                                </div>
                                <span className="text-[0.6rem] text-slate-500 ml-auto">Qty {product.quantity}</span>
                              </div>
                              {replacement.substituteNote && (
                                <>
                                  <div className="text-[0.6rem] font-semibold text-red-500 mb-0.5">Replacement Note</div>
                                  <div className="text-[0.55rem] text-slate-500 leading-relaxed">{replacement.substituteNote}</div>
                                </>
                              )}
                              <div className="absolute bottom-[-5px] right-4 border-[5px] border-transparent border-t-white" />
                            </div>
                          </div>
                        </div>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-[0.6rem] font-semibold bg-slate-100 text-slate-500">Regular</span>
                      )}
                    </td>
                    {isSubstitutionAllowed && (
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <button
                            className="w-7 h-7 rounded-lg border border-slate-200 bg-white text-slate-500 flex items-center justify-center hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-600 transition-colors cursor-pointer"
                            title="Replace"
                            onClick={() => setReplacingProductId(product.productId)}
                          >
                            <ArrowLeftRight size={13} />
                          </button>
                          {isReplaced && (
                            <button
                              className="w-7 h-7 rounded-lg border border-red-200 bg-white text-red-500 flex items-center justify-center hover:border-red-300 hover:bg-red-50 hover:text-red-600 transition-colors cursor-pointer"
                              title="Revert to Original"
                              onClick={() => onRevert(product.productId)}
                            >
                              <Undo size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Replacement Modal */}
      {isSubstitutionAllowed && replacingProductId && replacingProduct && (
        <ReplacementModal
          onClose={() => setReplacingProductId(null)}
          clinicId={clinicId}
          originalProduct={{
            name: replacingProduct.name,
            image: replacingProduct.productImage,
            manufacturer: "",
            quantity: replacingProduct.quantity,
          }}
          initialResults={initialClinicStock}
          onConfirm={(data) => {
            onReplace(replacingProductId, {
              originalProductId: replacingProductId,
              originalName: replacingProduct.name,
              originalImage: replacingProduct.productImage,
              originalManufacturer: "",
              originalQuantity: replacingProduct.quantity,
              ...data,
            });
            setReplacingProductId(null);
          }}
        />
      )}
    </div>
  );
};

export default ApproveOrderTable;
